import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled from 'styled-components';

import { TabContainer, Tab, Sizes, isWidget } from '@ratehub/base-ui';
import { SCENARIOS, REWARD_TYPES, ANNUAL_FEE_OPTIONS } from '@ratehub/cc-common';

import PAGE_TEMPLATES from '../definitions/PageTemplates';
import { PROVIDERS } from '../definitions/Providers';
import HeadingPropTypes from '../definitions/HeadingPropTypes';
import HeadingBlock from './HeadingBlock';
import ProductTableCreditCardScenario from './ProductTableCreditCardScenario';
import AdBigbox from './AdBigbox';
import AdvertisingDisclosure from './AdvertisingDisclosure';


function TabbedProductTableCreditCardScenario({
    heading,
    orientation = ORIENTATIONS.HORIZONTAL,
    initialTabIndex = 0,
    tables,
    hasAd = true,
    forceRenderTabs = false,
    className,
    ...otherProps
}) {
    const isAWidget = isWidget();

    return (
        <div className={className}>
            <HeadingBlock
                {...heading}
            />

            <div className="tab-wrapper rh-display-flex rh-flex-nowrap rh-justify-content-space-around">
                <div className="column-main rh-flex-grow-1 rh-width-100p">
                    <AdvertisingDisclosure
                        className="rh-display-block rh-ml-1_5"
                        size="extra-small"
                    />

                    <TabContainer
                        className="tab-container rh-flex-grow-1"
                        orientation={orientation}
                        initialTabIndex={initialTabIndex}
                        forceRenderTabs={forceRenderTabs}
                        {...otherProps}
                    >
                        <For
                            each="table"
                            of={tables}
                        >
                            <Tab
                                title={table.tabTitle}
                                key={table.initialScenario + table.initialRewardType}
                            >
                                <ProductTableCreditCardScenario
                                    showAdDisclosure={false}
                                    {...table}
                                />
                            </Tab>
                        </For>
                    </TabContainer>
                </div>

                {/* Might have other things in the future - for now, just AdBigbox */}
                <If condition={hasAd && !isAWidget}>
                    <aside className={classNames(
                        'column-sidebar rh-position-relative rh-mt-3 rh-mb-1_5 rh-mx-1_5',
                        'rh-display-flex rh-justify-content-center rh-align-items-flex-start')}
                    >
                        <AdBigbox alignment={AdBigbox.ALIGNMENTS.LEFT} />
                    </aside>
                </If>
            </div>
        </div>
    );
}


const ORIENTATIONS = {
    HORIZONTAL: 'horizontal',
    VERTICAL: 'vertical',
};
const MEDIA_QUERY_SIZE = '76.625em';


TabbedProductTableCreditCardScenario.propTypes = {
    heading: HeadingPropTypes,
    orientation: PropTypes.oneOf(Object.values(ORIENTATIONS)),
    initialTabIndex: PropTypes.number,
    tables: PropTypes.arrayOf(PropTypes.shape({
        tabTitle: PropTypes.string.isRequired,
        title: PropTypes.string,
        initialProvinceCode: PropTypes.string,
        initialScenario: PropTypes.oneOf(Object.values(SCENARIOS)),
        initialRewardType: PropTypes.oneOf(Object.values(REWARD_TYPES)),
        initialMonthlySpendingAverage: PropTypes.number,
        initialAnnualFee: PropTypes.oneOf(Object.values(ANNUAL_FEE_OPTIONS)),
        initialProvider: PropTypes.oneOf(PROVIDERS),
        initialCards: PropTypes.any,
    })).isRequired,
    hasAd: PropTypes.bool,
    forceRenderTabs: PropTypes.bool,
    className: PropTypes.string,
};

TabbedProductTableCreditCardScenario.blockKey = 'rh/tabbed-product-table-credit-card-scenario';
TabbedProductTableCreditCardScenario.hasHeading = true;
TabbedProductTableCreditCardScenario.requiresLayoutRow = true;
TabbedProductTableCreditCardScenario.supportedPageTypes = [ PAGE_TEMPLATES.creditCards ];


export default styled(TabbedProductTableCreditCardScenario)`
    > .tab-wrapper {
        flex-direction: column;

        margin-left: -${Sizes.SPACING.ONE_AND_A_HALF};
        margin-right: -${Sizes.SPACING.ONE_AND_A_HALF};

        @media screen and (min-width: ${MEDIA_QUERY_SIZE}) {
            flex-direction: row;
        }

        > .column-main {
            > .tab-container {
                margin-left: ${Sizes.SPACING.ONE_AND_A_HALF};
                margin-right: ${Sizes.SPACING.ONE_AND_A_HALF};
            }
        }

        > .column-sidebar {
            flex-basis: 300px;
        }
    }
`;
